import type { Position } from "@/types/game.types"

const DEFAULT_CELL_SIZE = 128

// Cell coordinates are packed into a single integer key. String keys meant
// every lookup allocated a string, and the hot path (one lookup per projectile
// per frame, times nine neighbouring cells) churned thousands of them a second.
const CELL_KEY_OFFSET = 512 // supports cell coords in [-512, 511]
const CELL_KEY_STRIDE = 4096

export interface SpatialGrid<T extends { position: Position; id: string }> {
  cells: Map<number, T[]>
  cellSize: number
}

/**
 * Create an empty spatial grid
 */
export function createSpatialGrid<T extends { position: Position; id: string }>(
  cellSize: number = DEFAULT_CELL_SIZE
): SpatialGrid<T> {
  return {
    cells: new Map(),
    cellSize,
  }
}

/**
 * Pack cell coordinates into a single numeric key
 */
function packCellKey(cellX: number, cellY: number): number {
  return (cellX + CELL_KEY_OFFSET) * CELL_KEY_STRIDE + (cellY + CELL_KEY_OFFSET)
}

/**
 * Get the cell key for a position
 */
export function getCellKey(x: number, y: number, cellSize: number): number {
  return packCellKey(Math.floor(x / cellSize), Math.floor(y / cellSize))
}

/**
 * Clear the grid
 */
export function clearGrid<T extends { position: Position; id: string }>(
  grid: SpatialGrid<T>
): void {
  grid.cells.clear()
}

/**
 * Insert an entity into the grid
 */
export function insertEntity<T extends { position: Position; id: string }>(
  grid: SpatialGrid<T>,
  entity: T
): void {
  const key = getCellKey(entity.position.x, entity.position.y, grid.cellSize)
  const cell = grid.cells.get(key)
  if (cell) {
    cell.push(entity)
  } else {
    grid.cells.set(key, [entity])
  }
}

/**
 * Rebuild the grid from a list of entities
 */
export function rebuildGrid<T extends { position: Position; id: string }>(
  grid: SpatialGrid<T>,
  entities: T[]
): void {
  clearGrid(grid)
  for (let i = 0; i < entities.length; i++) {
    insertEntity(grid, entities[i])
  }
}

/**
 * Get all entities in nearby cells (for collision detection)
 * Checks the cell containing the position plus all adjacent cells
 */
export function getNearbyEntities<T extends { position: Position; id: string }>(
  grid: SpatialGrid<T>,
  position: Position,
  radius: number = 0,
  out?: T[]
): T[] {
  const { cellSize } = grid
  const result = out ?? []
  result.length = 0

  // Calculate how many cells the radius spans - always include adjacent cells
  // (as documented above) so entities just across a cell boundary aren't missed
  const cellRadius = Math.max(1, Math.ceil(radius / cellSize))

  const centerCellX = Math.floor(position.x / cellSize)
  const centerCellY = Math.floor(position.y / cellSize)

  // Check center cell and surrounding cells based on radius
  for (let dx = -cellRadius; dx <= cellRadius; dx++) {
    for (let dy = -cellRadius; dy <= cellRadius; dy++) {
      const cell = grid.cells.get(packCellKey(centerCellX + dx, centerCellY + dy))
      if (cell) {
        for (let i = 0; i < cell.length; i++) {
          result.push(cell[i])
        }
      }
    }
  }

  return result
}

/**
 * Get entities within a specific radius (with actual distance check)
 * More precise than getNearbyEntities but slightly slower
 */
export function getEntitiesInRadius<T extends { position: Position; id: string }>(
  grid: SpatialGrid<T>,
  position: Position,
  radius: number
): T[] {
  const candidates = getNearbyEntities(grid, position, radius)
  const radiusSquared = radius * radius
  const result: T[] = []

  for (let i = 0; i < candidates.length; i++) {
    const entity = candidates[i]
    const dx = entity.position.x - position.x
    const dy = entity.position.y - position.y
    if (dx * dx + dy * dy <= radiusSquared) {
      result.push(entity)
    }
  }

  return result
}
